/**
 * workspace-bootstrap.ts  (Sprint BOOT-01)
 *
 * Sovereign Workspace Bootstrap Orchestrator.
 * Coordinates platform init, identity resolution, data mode, storage migration,
 * directory hydration, dependency resolution, and readiness verification.
 */

import { DATA_MODE, type DataMode } from '../config/data-mode.js';
import { globalWorkspaceDirectory } from '../mesh/governance/workspace-directory.js';
import {
  LocalStorageWorkspaceConfigStorage,
  createDefaultWorkspaceConfig,
  type WorkspaceConfiguration,
} from './workspace-configuration.js';
import { globalReadinessEvaluator, type PluginReadiness } from './workspace-readiness.js';

export type WorkspaceLifecycle =
  | 'uninitialized'         // No cryptographic owner; launch Genesis / Setup
  | 'identity-ready'        // Owner keys active; initializing storage
  | 'storage-ready'         // Migrations passed; hydrating directory
  | 'directory-ready'       // Members ready; resolving plugin dependency graph
  | 'dependencies-pending'  // Plugins evaluating manifests / wizard required
  | 'ready'                 // Healthy operations; launch main shell
  | 'degraded'              // Running with degraded capabilities
  | 'migration-required'    // Schema mismatch requiring manual confirmation
  | 'recovery-required';    // Cryptographic keylock / recovery needed

export interface BootstrapState {
  readonly lifecycle: WorkspaceLifecycle;
  readonly dataMode: DataMode;
  readonly ownerId?: string;
  readonly workspaceConfig?: WorkspaceConfiguration;
  readonly pluginReadiness: Map<string, PluginReadiness>;
  readonly degradedWarnings: readonly string[];
  readonly bootError?: string;
}

export class WorkspaceBootstrapper {
  private state: BootstrapState = {
    lifecycle: 'uninitialized',
    dataMode: DATA_MODE,
    pluginReadiness: new Map(),
    degradedWarnings: [],
  };

  private readonly listeners = new Set<(state: BootstrapState) => void>();

  getState(): BootstrapState {
    return this.state;
  }

  subscribe(callback: (state: BootstrapState) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private update(patch: Partial<BootstrapState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  async runBootstrap(ownerId?: string, displayName?: string): Promise<BootstrapState> {
    try {
      // Stage 1: Platform Initialization
      this.update({ lifecycle: 'uninitialized' });

      // Stage 2: Identity Resolution
      if (!ownerId) {
        // Uninitialized sovereign box
        this.update({ lifecycle: 'uninitialized' });
        return this.state;
      }

      this.update({ ownerId, lifecycle: 'identity-ready' });

      // Stage 3: Data Mode Selection (DATA_MODE is statically bound)
      const dataMode = DATA_MODE;

      // Stage 4: Storage Migration & Config Loading
      this.update({ lifecycle: 'storage-ready' });
      let config = LocalStorageWorkspaceConfigStorage.load(ownerId);

      if (!config) {
        config = createDefaultWorkspaceConfig(ownerId, ownerId);
        if (dataMode === 'demo') {
          config.setupStatus = 'active';
          config.recoveryMethodsConfigured = 1;
        }
        LocalStorageWorkspaceConfigStorage.save(config);
      } else {
        // Ensure default pluginSettings are present if not explicitly set
        if (!config.pluginSettings || Object.keys(config.pluginSettings).length === 0) {
          config.pluginSettings = {
            'CrmPlugin.configure-pipeline': true,
            'ActionPlannerPlugin.configure-workweek': true,
            'LLMWikiPlugin.configure-sources': true,
            ...config.pluginSettings,
          };
          LocalStorageWorkspaceConfigStorage.save(config);
        }
      }

      // Stage 5: Workspace Directory Hydration
      const workspaceId = ownerId;
      const selfInDir = globalWorkspaceDirectory
        .getMembers(workspaceId)
        .some((m) => m.principalId === ownerId);

      if (!selfInDir) {
        globalWorkspaceDirectory.addMember({
          principalId: ownerId,
          displayName: displayName || `Admin ${ownerId.slice(0, 6)}`,
          hnsName: `hns://${ownerId}.mesh`,
          role: 'admin',
          status: 'active',
          workspaceIds: [workspaceId, '*'],
          canReceiveAssignments: true,
          canBeMentioned: true,
        });
      }

      this.update({ lifecycle: 'directory-ready', workspaceConfig: config });

      // Stage 6: Plugin Dependency Resolution & Readiness Evaluation
      const readinessMap = globalReadinessEvaluator.evaluateAll(config);
      const warnings: string[] = [];
      let anyDegraded = false;
      let anyBlocked = false;

      for (const [pluginId, r] of readinessMap) {
        if (r.status === 'degraded') {
          anyDegraded = true;
          warnings.push(...r.degradedMessages);
        } else if (r.status === 'blocked') {
          anyBlocked = true;
          warnings.push(...r.degradedMessages);
        }
      }

      // Stage 7: Owner Readiness Check
      if (config.setupStatus !== 'active') {
        this.update({
          lifecycle: 'dependencies-pending',
          pluginReadiness: readinessMap,
          degradedWarnings: warnings,
        });
        return this.state;
      }

      // Stage 8: AppShell Ready Transition
      const finalLifecycle: WorkspaceLifecycle =
        anyBlocked || anyDegraded ? 'degraded' : 'ready';

      this.update({
        lifecycle: finalLifecycle,
        pluginReadiness: readinessMap,
        degradedWarnings: warnings,
      });

      return this.state;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      this.update({
        lifecycle: 'recovery-required',
        bootError: errorMsg,
      });
      return this.state;
    }
  }

  configurePluginSetup(pluginId: string, stepId: string, value: unknown = true): void {
    if (!this.state.workspaceConfig) return;
    const nextConfig: WorkspaceConfiguration = {
      ...this.state.workspaceConfig,
      pluginSettings: {
        ...this.state.workspaceConfig.pluginSettings,
        [`${pluginId}.${stepId}`]: value,
      },
      updatedAt: new Date().toISOString(),
    };

    LocalStorageWorkspaceConfigStorage.save(nextConfig);
    const readinessMap = globalReadinessEvaluator.evaluateAll(nextConfig);
    const warnings: string[] = [];
    let anyDegraded = false;
    let anyBlocked = false;

    for (const [, r] of readinessMap) {
      if (r.status === 'degraded') {
        anyDegraded = true;
        warnings.push(...r.degradedMessages);
      } else if (r.status === 'blocked') {
        anyBlocked = true;
        warnings.push(...r.degradedMessages);
      }
    }

    this.update({
      workspaceConfig: nextConfig,
      lifecycle: anyBlocked || anyDegraded ? 'degraded' : 'ready',
      pluginReadiness: readinessMap,
      degradedWarnings: warnings,
    });
  }

  completeWizard(activatedConfig: WorkspaceConfiguration): void {
    LocalStorageWorkspaceConfigStorage.save(activatedConfig);
    const readinessMap = globalReadinessEvaluator.evaluateAll(activatedConfig);
    const warnings: string[] = [];
    let anyDegraded = false;

    for (const [, r] of readinessMap) {
      if (r.status === 'degraded' || r.status === 'blocked') {
        anyDegraded = true;
        warnings.push(...r.degradedMessages);
      }
    }

    this.update({
      workspaceConfig: activatedConfig,
      lifecycle: anyDegraded ? 'degraded' : 'ready',
      pluginReadiness: readinessMap,
      degradedWarnings: warnings,
    });
  }
}

export const globalWorkspaceBootstrapper = new WorkspaceBootstrapper();
